import {
  getAttributes,
  Editor,
  markInputRule,
  mergeAttributes,
} from '@tiptap/core'
import { Link } from '@tiptap/extension-link'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { MarkType } from '@tiptap/pm/model'
import { find } from 'linkifyjs'
import { commandKeyPressed } from '~/lib/command-key-pressed'

export const CustomLink = Link.extend({
  inclusive: true,
  addInputRules() {
    return [
      markInputRule({
        find: /(?:\[(.+?)\]\((.+?)\))/,
        type: this.type,
        getAttributes(match) {
          const [, , href] = match
          return { href }
        },
      }),
    ]
  },
  addAttributes() {
    return {
      target: {
        default: undefined,
        parseHTML: (element) => {
          return element.getAttribute('target')
        },
        renderHTML: (attributes) => {
          if (!attributes.target) {
            return {}
          }
          return {
            target: attributes.target,
          }
        },
        rendered: false,
      },
      href: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('href')
        },
        renderHTML: (attributes) => {
          if (!attributes.href) {
            return {}
          }
          return {
            href: attributes.href,
          }
        },
        isRequired: true,
        keepOnSplit: false,
        rendered: true,
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ]
  },
  parseHTML() {
    return [
      {
        tag: 'a',
        getAttrs: (dom) => {
          if (!(dom instanceof HTMLElement)) {
            return {}
          }
          const href = dom.getAttribute('href')
          const target = dom.getAttribute('target')
          return {
            href,
            target,
          }
        },
      },
    ]
  },
  addProseMirrorPlugins() {
    return [pasteHandler(this), clickHandler()]
  },
}).configure({
  autolink: false,
  openOnClick: false,
})

export function clickHandler(): Plugin {
  return new Plugin({
    key: new PluginKey('handleClickLink'),
    props: {
      handleClick: (view, pos, event) => {
        if (event.button !== 0 || !commandKeyPressed(event)) {
          return false
        }

        const attrs = getAttributes(view.state, 'link')
        const link = (event.target as HTMLElement)?.closest('a')

        const href = link?.href ?? attrs.href
        const target = '_blank'

        if (link && href) {
          window.open(href, target)

          return true
        }

        return false
      },
    },
  })
}

type PasteHandlerOptions = {
  editor: Editor
  type: MarkType
}

export function pasteHandler(options: PasteHandlerOptions): Plugin {
  return new Plugin({
    key: new PluginKey('handlePasteLink'),
    props: {
      handlePaste: (view, event, slice) => {
        const { state } = view
        const { selection } = state
        const { empty } = selection

        if (empty) {
          return false
        }

        let textContent = ''

        slice.content.forEach((node) => {
          textContent += node.textContent
        })

        const link = find(textContent).find(
          (item) => item.isLink && item.value === textContent
        )

        if (!textContent || !link) {
          return false
        }

        options.editor.commands.setMark(options.type, {
          href: link.href,
        })

        return true
      },
    },
  })
}
